import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useServerExport, ServerExportStatus } from 'lib/hooks/useServerExport';
import request from 'lib/request';
import {
  mapSlotNRIResponseToSlotNRI,
  mapSlotNRIResponseToTractNRI,
} from 'utils/misc';
import { apiConfig } from 'types/api-config';
import { SlotVolumeSource, SlotAfeSource } from 'types/evaluation';
import { AggregationPeriod } from 'types/misc';
import { parseMomentUtc } from 'utils/dates';
import { parseMsgpackResponse } from 'utils/serialization';
import { SlotVolumeSourceConfiguration } from './types';
import type { SlotDateOverride } from '../../eval-3-workspace/types';
import {
  dpsScheduleResultAtom,
  workingSlotDateConfigurationsAtom,
  workingAbsoluteMaxDateAtom,
  workingAbsoluteMinDateAtom,
  workingDateRangeFilterTypeAtom,
  workingIntervalsPostAtom,
  workingIntervalsPreAtom,
} from '../../eval-3-workspace/atoms';
import type {
  CashflowRow,
  CashflowsPaginatedResponse,
  CashflowsFilterConfig,
  CashflowsSortConfig,
  RiskBanner,
  SlotNRIPaginatedResponse,
} from 'types/api-responses';
import { TractNRI } from 'components/nri-table/types';

// Constant for initial page cursor (no cursor = first page)
const DEFAULT_SERVER_PAGE_SIZE = 500;
// Debounce delay for search input
const SEARCH_DEBOUNCE_MS = 300;

interface UseCashflowsByMonthPaginatedOptions {
  calculationId?: string;
  serverPageSize?: number;
}

interface UseCashflowsByMonthPaginatedReturn {
  // All accumulated rows
  rows: CashflowRow[];
  isLoading: boolean;
  isFetchingMore: boolean;
  error: string | null;

  // Indicates if there's more data available on the server
  hasMore: boolean;

  // Fetch next page of data (called by DataTable when user scrolls near bottom)
  fetchNextPage: () => void;

  // Server-side sort/filter/search (clears accumulated data)
  setSort: (sort: CashflowsSortConfig[]) => void;
  setFilters: (filters: CashflowsFilterConfig[]) => void;
  setSearch: (search: string) => void;
  currentSort: CashflowsSortConfig[];
  currentFilters: CashflowsFilterConfig[];
  currentSearch: string;
}

export const useCashflowsByMonthPaginated = ({
  calculationId,
  serverPageSize = DEFAULT_SERVER_PAGE_SIZE,
}: UseCashflowsByMonthPaginatedOptions): UseCashflowsByMonthPaginatedReturn => {
  // Accumulated rows from all fetched server pages
  const [accumulatedRows, setAccumulatedRows] = useState<CashflowRow[]>([]);

  // Cursor for fetching next server page
  const [nextServerCursor, setNextServerCursor] = useState<string | null>(null);

  // Whether there are more server pages available
  const [hasMore, setHasMore] = useState<boolean>(false);

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Server-side sort, filter, and search
  const [currentSort, setCurrentSort] = useState<CashflowsSortConfig[]>([]);
  const [currentFilters, setCurrentFilters] = useState<CashflowsFilterConfig[]>(
    []
  );
  const [currentSearch, setCurrentSearch] = useState<string>('');

  // Debounced search value (what actually gets sent to server)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AbortController for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track which calculationId we've already fetched to prevent duplicate requests
  const lastFetchedCalcIdRef = useRef<string | undefined>(undefined);

  // Keep a ref to calculationId to avoid stale closure issues
  const calculationIdRef = useRef<string | undefined>(calculationId);
  calculationIdRef.current = calculationId;

  // Fetch a page from the API
  const fetchServerPage = useCallback(
    async (
      cursor: string | null,
      signal?: AbortSignal
    ): Promise<CashflowsPaginatedResponse | null> => {
      // Use ref to get current calculationId to avoid stale closures
      const currentCalcId = calculationIdRef.current;
      if (!currentCalcId) {
        return null;
      }

      const requestBody: Record<string, unknown> = {
        calculationId: currentCalcId,
        limit: serverPageSize,
      };

      // Only include cursor if not fetching first page
      if (cursor) {
        requestBody.cursor = cursor;
      }

      // Include sort and filters if set
      if (currentSort.length > 0) {
        requestBody.sort = currentSort;
      }
      if (currentFilters.length > 0) {
        requestBody.filters = currentFilters;
      }

      // Include search term if set (server-side text search)
      if (debouncedSearch) {
        requestBody.search = debouncedSearch;
      }

      const csrfToken =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content') || '';

      const response = await fetch(
        apiConfig.AGGREGATED_FREE_CASHFLOW_CASHFLOWS_BY_MONTH_URL,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify(requestBody),
          signal,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch cashflows data');
      }

      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/x-msgpack')) {
        return (await parseMsgpackResponse(
          response
        )) as CashflowsPaginatedResponse;
      }
      return (await response.json()) as CashflowsPaginatedResponse;
    },
    [serverPageSize, currentSort, currentFilters, debouncedSearch]
  );

  // Fetch and append more data
  const fetchMore = useCallback(async () => {
    if (!hasMore || !nextServerCursor || isFetchingMore) {
      return;
    }

    setIsFetchingMore(true);

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const result = await fetchServerPage(nextServerCursor, controller.signal);

      if (controller.signal.aborted) return;

      if (!result) {
        setIsFetchingMore(false);
        return;
      }

      // Append new rows to accumulated rows
      setAccumulatedRows((prev) => [...prev, ...result.rows]);
      setNextServerCursor(result.meta.nextCursor);
      setHasMore(result.meta.hasMore);
      setIsFetchingMore(false);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }

      const errorMessage =
        err instanceof Error ? err.message : 'An unknown error occurred';
      console.error(
        '[useCashflowsByMonthPaginated] fetchMore error:',
        errorMessage
      );
      setError(errorMessage);
      setIsFetchingMore(false);
    }
  }, [hasMore, nextServerCursor, fetchServerPage, isFetchingMore]);

  // Track previous filter/sort/search values to detect changes
  const prevFiltersRef = useRef<string>('');
  const prevSortRef = useRef<string>('');
  const prevSearchRef = useRef<string>('');

  // Fetch data helper - shared between initial fetch and filter/sort/search changes
  const fetchInitialData = useCallback(
    async (signal: AbortSignal) => {
      setIsLoading(true);
      try {
        const result = await fetchServerPage(null, signal);

        if (signal.aborted) {
          return;
        }

        if (result) {
          setAccumulatedRows(result.rows);
          setNextServerCursor(result.meta.nextCursor);
          setHasMore(result.meta.hasMore);
        }
        setIsLoading(false);
      } catch (err) {
        if (signal.aborted) return;
        if (err instanceof Error && err.name === 'AbortError') return;

        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred';
        setError(errorMessage);
        setIsLoading(false);
      }
    },
    [fetchServerPage]
  );

  // Initial fetch effect - runs when calculationId changes
  useEffect(() => {
    if (!calculationId || calculationId === lastFetchedCalcIdRef.current) {
      return;
    }

    lastFetchedCalcIdRef.current = calculationId;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Reset state for new calculationId
    setAccumulatedRows([]);
    setNextServerCursor(null);
    setHasMore(false);
    setError(null);

    // Initialize prev refs
    prevFiltersRef.current = JSON.stringify(currentFilters);
    prevSortRef.current = JSON.stringify(currentSort);
    prevSearchRef.current = debouncedSearch;

    fetchInitialData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [
    calculationId,
    fetchInitialData,
    currentFilters,
    currentSort,
    debouncedSearch,
  ]);

  // Re-fetch when filters, sort, or search change (after initial fetch)
  useEffect(() => {
    // Skip if no calculationId or we haven't done initial fetch yet
    if (!calculationId || !lastFetchedCalcIdRef.current) {
      return;
    }

    const filtersJson = JSON.stringify(currentFilters);
    const sortJson = JSON.stringify(currentSort);

    // Check if any of the query params changed
    const filtersChanged = filtersJson !== prevFiltersRef.current;
    const sortChanged = sortJson !== prevSortRef.current;
    const searchChanged = debouncedSearch !== prevSearchRef.current;

    if (!filtersChanged && !sortChanged && !searchChanged) {
      return;
    }

    // Update prev refs
    prevFiltersRef.current = filtersJson;
    prevSortRef.current = sortJson;
    prevSearchRef.current = debouncedSearch;

    // Abort any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Reset accumulated data
    setAccumulatedRows([]);
    setNextServerCursor(null);
    setHasMore(false);
    setError(null);

    fetchInitialData(controller.signal);

    return () => {
      controller.abort();
    };
  }, [
    calculationId,
    currentFilters,
    currentSort,
    debouncedSearch,
    fetchInitialData,
  ]);

  // Sort and filter setters - just update state, effect handles re-fetch
  const setSort = useCallback((sort: CashflowsSortConfig[]) => {
    setCurrentSort(sort);
  }, []);

  const setFilters = useCallback((filters: CashflowsFilterConfig[]) => {
    setCurrentFilters(filters);
  }, []);

  // Search setter with debouncing - just updates state, effect handles re-fetch
  const setSearch = useCallback(
    (search: string) => {
      // Skip if search value hasn't changed (prevents resetting on mount)
      if (search === currentSearch) {
        return;
      }

      setCurrentSearch(search);

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce the actual search request
      searchTimeoutRef.current = setTimeout(() => {
        if (search !== debouncedSearch) {
          setDebouncedSearch(search);
        }
      }, SEARCH_DEBOUNCE_MS);
    },
    [currentSearch, debouncedSearch]
  );

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    rows: accumulatedRows,
    isLoading,
    isFetchingMore,
    error,
    hasMore,
    fetchNextPage: fetchMore,
    setSort,
    setFilters,
    setSearch,
    currentSort,
    currentFilters,
    currentSearch,
  };
};

/**
 * Resolves the visible date range for FCF charts based on the current
 * date range filter settings. Reads working copy atoms via packageId.
 *
 * @param {number} packageId - The package ID for scoping working copy atoms.
 * @param {string} effectiveDate - The applied effective date.
 * @param {AggregationPeriod} aggregation - The time interval grouping.
 * @param {boolean} isSplit - Whether the effective date falls mid-interval.
 * @return {{ minDate: Moment, maxDate: Moment }} The resolved date range.
 */
export const useResolveDateRange = (
  packageId: number,
  effectiveDate: string,
  aggregation: AggregationPeriod,
  isSplit: boolean
) => {
  const absoluteMinDate = useAtomValue(workingAbsoluteMinDateAtom(packageId));
  const absoluteMaxDate = useAtomValue(workingAbsoluteMaxDateAtom(packageId));
  const intervalsPostEffectiveDate = useAtomValue(
    workingIntervalsPostAtom(packageId)
  );
  const intervalsPreEffectiveDate = useAtomValue(
    workingIntervalsPreAtom(packageId)
  );
  const dateRangeFilterType = useAtomValue(
    workingDateRangeFilterTypeAtom(packageId)
  );

  const [relativeMinDate, relativeMaxDate] = [
    parseMomentUtc(effectiveDate)
      .startOf(aggregation)
      .subtract(intervalsPreEffectiveDate, aggregation),
    parseMomentUtc(effectiveDate)
      .endOf(aggregation)
      .add(intervalsPostEffectiveDate - (isSplit ? 0 : 1), aggregation),
  ];

  if (dateRangeFilterType === 'absolute') {
    const [minDate, maxDate] =
      absoluteMinDate && absoluteMaxDate
        ? [
            parseMomentUtc(absoluteMinDate).startOf(aggregation),
            parseMomentUtc(absoluteMaxDate).endOf(aggregation),
          ]
        : [relativeMinDate, relativeMaxDate];
    return {
      minDate,
      maxDate,
    };
  } else {
    return {
      minDate: relativeMinDate,
      maxDate: relativeMaxDate,
    };
  }
};

// ─────────────────────────────────────
// Slot Allocations Export Hook
// ─────────────────────────────────────

// Re-export type for backwards compatibility
export type SlotAllocationsExportStatus = ServerExportStatus;

// Builds scheduledSlotsBlobPath and slotDateOverrides params for export requests.
// Returns an empty object when no DPS schedule is active.
const buildDateOverrideParams = (
  scheduledSlotsBlobPath?: string | null,
  slotDateConfigurations?: Record<string, SlotDateOverride>
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  if (scheduledSlotsBlobPath) {
    result.scheduledSlotsBlobPath = scheduledSlotsBlobPath;
  }
  if (
    slotDateConfigurations &&
    Object.keys(slotDateConfigurations).length > 0
  ) {
    result.slotDateOverrides = Object.entries(slotDateConfigurations)
      .filter(([, v]) => v !== undefined)
      .map(([slotId, dates]) => ({
        slot_id: slotId,
        permit_date: dates.permitDate,
        spud_date: dates.spudDate,
        first_production_date: dates.firstProductionDate,
      }));
  }
  return result;
};

/**
 * Hook for handling server-side slot allocations export with Pusher notifications.
 *
 * Uses the shared useServerExport hook with slot-allocations-specific configuration.
 * @param {number} packageId - The package ID to export slot allocations for
 * @return {UseServerExportReturn} The export status and control functions
 */
/**
 * Hook for handling server-side slot allocations export (37-column, one row per slot+tract).
 *
 * Uses the shared useServerExport hook with slot allocations-specific configuration.
 * @param {object} options - Export configuration options
 * @param {number} options.packageId - The package ID to export slot allocations for
 * @param {number} options.eval2EvaluationId - The actual Eval 2.0 evaluation ID for API calls
 * @param {SlotVolumeSource} options.slotVolumeSource - Slot volume source configuration
 * @param {Record<string, SlotVolumeSourceConfiguration>} options.slotVolumeConfigurations - Per-slot volume source overrides
 * @return {UseServerExportReturn} The export status and control functions
 */
export const useSlotAllocationsExport = ({
  packageId,
  eval2EvaluationId,
  calculationId,
  slotVolumeSource,
  slotAfeSource,
  dcaScenarioId,
  slotVolumeConfigurations,
}: {
  packageId: number;
  eval2EvaluationId: number;
  calculationId?: number | null;
  slotVolumeSource: SlotVolumeSource;
  slotAfeSource?: SlotAfeSource;
  dcaScenarioId?: number | null;
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
}) => {
  const dpsScheduleResult = useAtomValue(dpsScheduleResultAtom(packageId));
  const slotDateConfigurations = useAtomValue(
    workingSlotDateConfigurationsAtom(packageId)
  );

  return useServerExport({
    channel: `package_${packageId}`,
    event: 'slot_allocations_export_complete',
    exportFn: async (params) => {
      const csrfToken =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content') || '';

      // Convert slotVolumeConfigurations to array format
      const slotVolumeSources = Object.entries(slotVolumeConfigurations).map(
        ([slotId, config]) => ({
          slotId,
          volumeSourceId: config.volumeSourceId,
          ...(config.forecastSource
            ? { forecastSource: config.forecastSource }
            : {}),
          ...(config.dcaScenario ? { dcaScenario: config.dcaScenario } : {}),
        })
      );

      const response = await fetch(
        `/packages/${packageId}/enhanced_slot_allocations/export`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            evaluationId: eval2EvaluationId,
            ...(calculationId
              ? { evaluationCalculationId: calculationId }
              : {}),
            slotVolumeSource: slotVolumeSource,
            ...(slotAfeSource ? { slotAfeSource } : {}),
            ...(dcaScenarioId ? { dcaScenarioId } : {}),
            slotVolumeSources: slotVolumeSources,
            ...buildDateOverrideParams(
              dpsScheduleResult?.scheduledSlotsBlobPath,
              slotDateConfigurations
            ),
            ...params, // filters and sort
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start export');
      }

      const data = await response.json();
      return { exportId: data.exportId };
    },
  });
};

/**
 * Hook for handling server-side slots export (27-column, one row per slot).
 *
 * Uses the shared useServerExport hook with slots-specific configuration.
 * @param {object} options - Export configuration options
 * @param {number} options.packageId - The package ID to export slots for
 * @param {number} options.eval2EvaluationId - The actual Eval 2.0 evaluation ID for API calls
 * @param {SlotVolumeSource} options.slotVolumeSource - Slot volume source configuration
 * @param {Record<string, SlotVolumeSourceConfiguration>} options.slotVolumeConfigurations - Per-slot volume source overrides
 * @return {UseServerExportReturn} The export status and control functions
 */
export const useSlotsExport = ({
  packageId,
  eval2EvaluationId,
  calculationId,
  slotVolumeSource,
  slotAfeSource,
  dcaScenarioId,
  slotVolumeConfigurations,
}: {
  packageId: number;
  eval2EvaluationId: number;
  calculationId?: number | null;
  slotVolumeSource: SlotVolumeSource;
  slotAfeSource?: SlotAfeSource;
  dcaScenarioId?: number | null;
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
}) => {
  const dpsScheduleResult = useAtomValue(dpsScheduleResultAtom(packageId));
  const slotDateConfigurations = useAtomValue(
    workingSlotDateConfigurationsAtom(packageId)
  );

  return useServerExport({
    channel: `package_${packageId}`,
    event: 'slots_export_complete',
    exportFn: async (params) => {
      const csrfToken =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content') || '';

      // Convert slotVolumeConfigurations to array format
      const slotVolumeSources = Object.entries(slotVolumeConfigurations).map(
        ([slotId, config]) => ({
          slotId,
          volumeSourceId: config.volumeSourceId,
          ...(config.forecastSource
            ? { forecastSource: config.forecastSource }
            : {}),
          ...(config.dcaScenario ? { dcaScenario: config.dcaScenario } : {}),
        })
      );

      const response = await fetch(
        `/packages/${packageId}/enhanced_slot_allocations/slots_export`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
          body: JSON.stringify({
            evaluationId: eval2EvaluationId,
            ...(calculationId
              ? { evaluationCalculationId: calculationId }
              : {}),
            slotVolumeSource: slotVolumeSource,
            ...(slotAfeSource ? { slotAfeSource } : {}),
            ...(dcaScenarioId ? { dcaScenarioId } : {}),
            slotVolumeSources: slotVolumeSources,
            ...buildDateOverrideParams(
              dpsScheduleResult?.scheduledSlotsBlobPath,
              slotDateConfigurations
            ),
            ...params, // filters and sort
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start export');
      }

      const data = await response.json();
      return { exportId: data.exportId };
    },
  });
};

// ─────────────────────────────────────
// Enhanced Slot Allocations Paginated Hook
// ─────────────────────────────────────

const DEFAULT_ENHANCED_SLOTS_PAGE_SIZE = 100;

// Filter value can be a string (text search) or range object
type FilterValue = string | { min?: number | string; max?: number | string };

interface SlotAllocationFilters {
  [key: string]: FilterValue;
}

interface SlotAllocationSort {
  column: string;
  direction: 'asc' | 'desc';
}

interface UseEnhancedSlotAllocationsPaginatedOptions {
  packageId: number;
  evalId?: number;
  /** The actual Eval 2.0 evaluation ID, always sent as evaluationId to the API when available (for projected LTD dates and eval_2_0 sources). */
  eval2EvaluationId?: number | null;
  /** Current slot volume source setting. */
  slotVolumeSource: SlotVolumeSource;
  /** Current AFE source setting. */
  slotAfeSource?: SlotAfeSource;
  /** Selected DCA scenario ID (null = Legacy). */
  dcaScenarioId?: number | null;
  /** Eval3 calculation ID — when present, serves from cached Parquet blob instead of live DB query. */
  calculationId?: number | null;
  pageSize?: number;
  /** Server-side filters */
  filters?: SlotAllocationFilters;
  /** Server-side sort */
  sort?: SlotAllocationSort;
  /** Current page offset (0-based) */
  offset?: number;
  /** Mapping mode: 'slot' = one row per slot (default), 'tract' = one row per slot+tract pair */
  mapMode?: 'slot' | 'tract';
}

interface UseEnhancedSlotAllocationsPaginatedReturn {
  /** Current page of slot rows */
  rows: TractNRI[];
  /** Initial loading state */
  isLoading: boolean;
  /** Loading more pages in background (legacy mode only) */
  isFetchingMore: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether there are more pages to fetch */
  hasMore: boolean;
  /** Total count of slots from the server */
  totalCount: number;
  /** Count of PDP wells without the primary forecast for the selected scenario */
  undeclinedPdpWellCount: number;
  /** Asset-risk banner metadata (counts + quality breakdown) */
  riskBanner: RiskBanner | null;
  /** Fetch the next page when user scrolls near the bottom */
  fetchNextPage: () => void;
  /** Refetch with current params */
  refetch: () => void;
  /** Set the search term (debounced, triggers server-side search) */
  setSearch: (search: string) => void;
  /** Current search input value (immediate, for controlled input) */
  currentSearch: string;
}

/**
 * Paginated hook for enhanced slot allocations.
 *
 * Supports two modes:
 * 1. Server-side mode (when filters/sort provided): Fetches single page with SQL-level filtering/sorting
 * 2. Scroll mode (no filters/sort): Fetches pages on demand as user scrolls
 * @param {UseEnhancedSlotAllocationsPaginatedOptions} options - Hook configuration options
 * @return {UseEnhancedSlotAllocationsPaginatedReturn} The paginated slot allocations data
 */
export const useEnhancedSlotAllocationsPaginated = ({
  packageId,
  evalId,
  eval2EvaluationId,
  slotVolumeSource,
  slotAfeSource,
  dcaScenarioId,
  calculationId,
  pageSize = DEFAULT_ENHANCED_SLOTS_PAGE_SIZE,
  filters,
  sort,
  offset = 0,
  mapMode = 'slot',
}: UseEnhancedSlotAllocationsPaginatedOptions): UseEnhancedSlotAllocationsPaginatedReturn => {
  // Use a safe evalId for atoms (default to 0 if not provided)
  const safeEvalId = evalId ?? 0;

  // Search state with debouncing
  const [currentSearch, setCurrentSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Determine if we're in server-side mode (has filters, sort, or search)
  const isServerSideMode = Boolean(filters || sort || debouncedSearch);

  // Current displayed rows
  const [rows, setRows] = useState<TractNRI[]>([]);

  // Track offset for next page fetch
  const nextOffsetRef = useRef<number>(0);

  // Guard against concurrent fetchMore calls
  const isFetchingMoreRef = useRef<boolean>(false);

  // Total count from server
  const [totalCount, setTotalCount] = useState<number>(0);
  const [undeclinedPdpWellCount, setUndeclinedPdpWellCount] =
    useState<number>(0);

  // Asset-risk banner metadata (arrives on every fetch; only the initial page
  // needs to seed state since the banner counts are package-wide).
  const [riskBanner, setRiskBanner] = useState<RiskBanner | null>(null);

  // Whether there are more pages available
  const [hasMore, setHasMore] = useState<boolean>(false);

  // Loading states
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Track fetch key to detect parameter changes
  const fetchKeyRef = useRef<string>('');

  // AbortController ref for the current request
  const currentAbortController = useRef<AbortController | null>(null);

  // Build stable filter/sort strings for dependency tracking
  const filtersKey = filters ? JSON.stringify(filters) : '';
  const sortKey = sort ? JSON.stringify(sort) : '';

  // Fetch a page from the API
  const fetchPage = useCallback(
    async (
      pageOffset: number,
      customPageSize?: number
    ): Promise<SlotNRIPaginatedResponse | null> => {
      const requestBody: Record<string, unknown> = {
        slotVolumeSource,
        ...(slotAfeSource ? { slotAfeSource } : {}),
        ...(dcaScenarioId ? { dcaScenarioId } : {}),
        limit: customPageSize ?? pageSize,
        offset: pageOffset,
        computedOnly: false,
      };

      // Always send evaluationId when available — needed for projected dates on LTD slots
      // (via evaluation_output_slots), not just for eval_2_0 NRI/volume sources.
      if (eval2EvaluationId) {
        requestBody.evaluationId = eval2EvaluationId;
      }

      // When a calculation ID is available, send it so the backend serves from
      // the cached Parquet blob instead of running live database queries.
      if (calculationId) {
        requestBody.evaluationCalculationId = calculationId;
      }

      // Add server-side filtering/sorting if provided
      if (filters && Object.keys(filters).length > 0) {
        requestBody.filters = filters;
      }

      if (sort) {
        requestBody.sort = sort;
      }

      if (debouncedSearch) {
        requestBody.search = debouncedSearch;
      }

      // Abort any in-flight request
      if (currentAbortController.current) {
        currentAbortController.current.abort();
      }

      // Create a new AbortController for this request
      const abortController = new AbortController();
      currentAbortController.current = abortController;

      const response = await request.post<
        SlotNRIPaginatedResponse,
        { error: string }
      >(
        `/packages/${packageId}/enhanced_slot_allocations`,
        requestBody,
        abortController
      );

      if (response.type === 'abort') {
        return null;
      }

      if (response.type !== 'success') {
        const errorMessage =
          response.type === 'error' && response.data?.error
            ? response.data.error
            : 'Failed to fetch enhanced slot allocations';
        throw new Error(errorMessage);
      }

      return response.data;
    },
    [
      packageId,
      eval2EvaluationId,
      slotVolumeSource,
      slotAfeSource,
      dcaScenarioId,
      calculationId,
      pageSize,
      filters,
      sort,
      debouncedSearch,
    ]
  );

  // Load more rows when user scrolls - fetches next page directly from API
  const fetchMore = useCallback(async () => {
    if (isFetchingMoreRef.current || isServerSideMode) return;

    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);

    try {
      const result = await fetchPage(nextOffsetRef.current, pageSize);

      if (!result) {
        isFetchingMoreRef.current = false;
        setIsFetchingMore(false);
        return;
      }

      const mapFn =
        mapMode === 'tract'
          ? mapSlotNRIResponseToTractNRI
          : mapSlotNRIResponseToSlotNRI;
      const newRows = mapFn({ slots: result.slots });
      setRows((prev) => [...prev, ...newRows]);
      nextOffsetRef.current += result.slots.length;
      setHasMore(result.meta.hasMore);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        isFetchingMoreRef.current = false;
        setIsFetchingMore(false);
        return;
      }

      console.error(
        '[useEnhancedSlotAllocationsPaginated] fetchMore error:',
        err
      );
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  }, [isServerSideMode, fetchPage, pageSize, mapMode]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    fetchKeyRef.current = ''; // Force refetch
  }, []);

  // Initial fetch and refetch when params change
  useEffect(() => {
    // Build fetch key including filters/sort/search/offset for server-side mode
    const currentFetchKey = isServerSideMode
      ? `${packageId}-${safeEvalId}-${slotVolumeSource}-${slotAfeSource}-${dcaScenarioId}-${filtersKey}-${sortKey}-${debouncedSearch}-${offset}`
      : `${packageId}-${safeEvalId}-${slotVolumeSource}-${slotAfeSource}-${dcaScenarioId}`;

    // Skip if params haven't changed
    if (fetchKeyRef.current === currentFetchKey) {
      return;
    }
    fetchKeyRef.current = currentFetchKey;

    // Cancel any existing request
    if (currentAbortController.current) {
      currentAbortController.current.abort();
    }

    // Reset state
    setRows([]);
    setHasMore(false);
    setTotalCount(0);
    setUndeclinedPdpWellCount(0);
    setRiskBanner(null);
    setError(null);
    setIsLoading(true);

    // Reset scroll offset
    nextOffsetRef.current = 0;
    isFetchingMoreRef.current = false;

    const fetchInitial = async () => {
      try {
        // In server-side mode, use the provided offset; in legacy mode, start at 0
        const fetchOffset = isServerSideMode ? offset : 0;
        const result = await fetchPage(fetchOffset);

        if (!result) {
          setIsLoading(false);
          return;
        }

        const mapFn =
          mapMode === 'tract'
            ? mapSlotNRIResponseToTractNRI
            : mapSlotNRIResponseToSlotNRI;
        const transformedRows = mapFn({
          slots: result.slots,
        });
        setRows(transformedRows);
        setHasMore(result.meta.hasMore);
        setTotalCount(result.meta.totalCount ?? 0);
        setRiskBanner(result.riskBanner ?? null);
        setIsLoading(false);

        // Set offset for next page fetch
        nextOffsetRef.current = result.slots.length;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;

        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred';
        setError(errorMessage);
        setIsLoading(false);
      }
    };

    fetchInitial();

    return () => {
      if (currentAbortController.current) {
        currentAbortController.current.abort();
      }
    };
  }, [
    packageId,
    safeEvalId,
    slotVolumeSource,
    slotAfeSource,
    dcaScenarioId,
    fetchPage,
    isServerSideMode,
    filtersKey,
    sortKey,
    debouncedSearch,
    offset,
    mapMode,
  ]);

  // AbortController for background count fetching
  const countAbortController = useRef<AbortController | null>(null);
  // Deduplication key to prevent redundant count fetches (same pattern as fetchKeyRef)
  const countFetchKeyRef = useRef<string>('');

  // Fetch total count and undeclined PDP well count in background
  useEffect(() => {
    // Deduplicate: skip if the relevant params haven't actually changed
    const countFetchKey = `${packageId}-${dcaScenarioId}-${calculationId}-${JSON.stringify(
      filters
    )}-${debouncedSearch}`;
    if (countFetchKey === countFetchKeyRef.current) return;

    // Cancel any existing count request
    if (countAbortController.current) {
      countAbortController.current.abort();
    }

    const controller = new AbortController();
    countAbortController.current = controller;

    const fetchCount = async () => {
      try {
        const requestBody: Record<string, unknown> = {
          computedOnly: false,
        };

        // Add filters if present (for totalCount filtering)
        if (filters && Object.keys(filters).length > 0) {
          requestBody.filters = filters;
        }

        // Add search if present (for totalCount filtering)
        if (debouncedSearch) {
          requestBody.search = debouncedSearch;
        }

        // Add dcaScenarioId for undeclined PDP well count
        if (dcaScenarioId) {
          requestBody.dcaScenarioId = dcaScenarioId;
        }

        if (calculationId) {
          requestBody.evaluationCalculationId = calculationId;
        }

        const csrfToken =
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content') || '';

        const response = await fetch(
          `/packages/${packageId}/enhanced_slot_allocations/count`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken,
            },
            body: JSON.stringify(requestBody),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          console.warn(
            '[useEnhancedSlotAllocationsPaginated] Count request failed'
          );
          return;
        }

        const data = (await response.json()) as {
          totalCount: number;
          undeclinedPdpWellCount: number;
        };
        // Only mark as fetched after successful response, so aborted requests retry
        countFetchKeyRef.current = countFetchKey;
        setTotalCount(data.totalCount);
        setUndeclinedPdpWellCount(data.undeclinedPdpWellCount);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        console.warn(
          '[useEnhancedSlotAllocationsPaginated] Count fetch error:',
          err
        );
      }
    };

    fetchCount();

    return () => {
      controller.abort();
    };
  }, [packageId, dcaScenarioId, calculationId, filters, debouncedSearch]);

  // Search setter with debouncing
  const setSearch = useCallback(
    (search: string) => {
      if (search === currentSearch) {
        return;
      }

      setCurrentSearch(search);

      // Clear existing timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce the actual search request
      searchTimeoutRef.current = setTimeout(() => {
        setDebouncedSearch(search);
      }, SEARCH_DEBOUNCE_MS);
    },
    [currentSearch]
  );

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    rows,
    isLoading,
    isFetchingMore,
    error,
    hasMore,
    totalCount,
    undeclinedPdpWellCount,
    riskBanner,
    fetchNextPage: fetchMore,
    refetch,
    setSearch,
    currentSearch,
  };
};
